import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { catchError, distinctUntilChanged, forkJoin, of, Subject, switchMap } from 'rxjs';
import { ChangeCurrentSelectedDepartment } from '../../../tenant-selector/state/tenant-user-access.actions';
import {
  PermitDashboard,
  PermitState,
  PermitToWorkApiService,
  PermitWorkNotice,
} from '../../../permit-to-work-user/permit-to-work-api.service';
import { IdName } from '../../../shared/models/id-name';
import { WidgetBlock } from '../../../shared/ui-elements/ui-widget-card/ui-widget-card.component';
import { UserSettingsState } from '../../../tenant-selector/state/tenant-user-access.state';
import { Privileges } from '../../privileges/privileges';
import { DashboardDepartmentChangeService } from '../../services/dashboard-department-change.service';

interface DashboardScope {
  tenantId: string;
  departmentId: string;
  allowed: boolean;
  revision: number;
}

@Component({
  selector: 'app-permit-to-work-dashboard-widgets',
  templateUrl: './permit-to-work-dashboard-widgets.component.html',
  styleUrls: ['./permit-to-work-dashboard-widgets.component.scss'],
  standalone: false,
})
export class PermitToWorkDashboardWidgetsComponent implements OnInit {
  readonly departmentControl = new FormControl('', { nonNullable: true });
  readonly permitState = PermitState;
  departmentOptions: IdName[] = [];
  visible = false;
  canReadCounters = false;
  loading = false;
  countersFailed = false;
  noticesFailed = false;
  notices: PermitWorkNotice[] = [];
  forApproval: WidgetBlock[] = [];
  approved: WidgetBlock[] = [];
  rejected: number | null = null;
  finished: number | null = null;
  progress: { key: string; blocks: WidgetBlock[] }[] = [];
  private tenantId = '';
  private globalDepartmentId = '';
  private revision = 0;
  private readonly requests = new Subject<DashboardScope>();
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private store: Store,
    private api: PermitToWorkApiService,
    private departmentChanges: DashboardDepartmentChangeService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.listenToRequests();
    this.store
      .select((state: { userSettings: UserSettingsState }) => state.userSettings)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => this.syncScope(state));
    this.departmentControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => this.departmentChanges.setDashboardDepartment(id));
    this.departmentChanges
      .getDashboardDepartmentChanges()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => {
        if (!this.departmentOptions.some((department) => department.id === id)) return;
        this.departmentControl.setValue(id, { emitEvent: false });
        this.reload();
      });
  }

  retry(): void {
    this.reload(true);
  }

  openPermit(notice: PermitWorkNotice, event: MouseEvent): void {
    event.preventDefault();
    if (!this.canReadCounters) return;
    this.store
      .dispatch(new ChangeCurrentSelectedDepartment(this.departmentControl.value, false))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.router.navigate(['/permit-to-work', notice.permitId]));
  }

  private syncScope(state: UserSettingsState): void {
    const tenantId = state.currentTenant?.id ?? '';
    const departmentId = state.currentDepartment?.id ?? '';
    const changed = tenantId !== this.tenantId || departmentId !== this.globalDepartmentId;
    this.tenantId = tenantId;
    this.globalDepartmentId = departmentId;
    this.departmentOptions = state.availableDepartments;
    this.visible = !!tenantId;
    this.canReadCounters =
      state.userAccess?.isProductOwner === true ||
      state.privilegesFlat.includes(Privileges.PermitToWorkRead);
    if (
      changed ||
      !this.departmentOptions.some((option) => option.id === this.departmentControl.value)
    ) {
      const selected =
        this.departmentOptions.find((option) => option.id === departmentId) ??
        this.departmentOptions[0];
      this.departmentControl.setValue(selected?.id ?? '', { emitEvent: false });
    }
    this.reload();
  }

  private reload(force = false): void {
    if (force) this.revision++;
    this.requests.next({
      tenantId: this.tenantId,
      departmentId: this.departmentControl.value,
      allowed: this.canReadCounters,
      revision: this.revision,
    });
  }

  private listenToRequests(): void {
    this.requests
      .pipe(
        distinctUntilChanged(
          (previous, current) =>
            previous.tenantId === current.tenantId &&
            previous.departmentId === current.departmentId &&
            previous.allowed === current.allowed &&
            previous.revision === current.revision
        ),
        switchMap((scope) => {
          this.reset();
          if (!scope.tenantId || !scope.departmentId) return of(null);
          this.loading = true;
          return forkJoin({
            counters: scope.allowed
              ? this.api
                  .dashboard(scope.departmentId, scope.tenantId)
                  .pipe(catchError(() => of(null)))
              : of(null),
            notices: this.api
              .workNotices(scope.departmentId, scope.tenantId)
              .pipe(catchError(() => of(null))),
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.loading = false;
        if (!result) return;
        this.countersFailed = this.canReadCounters && result.counters === null;
        this.noticesFailed = result.notices === null;
        this.notices = result.notices ?? [];
        if (result.counters) this.applyCounters(result.counters);
      });
  }

  private reset(): void {
    this.loading = false;
    this.countersFailed = false;
    this.noticesFailed = false;
    this.notices = [];
    this.forApproval = this.pair('ForApproval', null, null, {
      icon: 'schedule',
      iconColor: 'orange',
    });
    this.approved = this.pair('Approved', null, null, { icon: 'check', iconColor: 'green' });
    this.rejected = null;
    this.finished = null;
    this.progress = [
      {
        key: 'Overdue',
        blocks: this.pair('InProgressOverdue', null, null, {
          icon: 'error_outline',
          iconColor: 'red',
        }),
      },
      {
        key: 'DueSoon',
        blocks: this.pair('InProgressDueSoon', null, null, {
          icon: 'schedule',
          iconColor: 'yellow',
        }),
      },
      {
        key: 'OnTime',
        blocks: this.pair('InProgressOnTime', null, null, { icon: 'sync', iconColor: 'cyan' }),
      },
    ];
  }

  private applyCounters(data: PermitDashboard): void {
    this.forApproval = this.pair('ForApproval', data.myForApproval, data.allForApproval, {
      icon: 'schedule',
      iconColor: 'orange',
    });
    this.approved = this.pair('Approved', data.myApproved, data.allApproved, {
      icon: 'check',
      iconColor: 'green',
    });
    this.rejected = data.myRejected;
    this.finished = data.allFinished;
    this.progress = [
      {
        key: 'Overdue',
        blocks: this.pair(
          'InProgressOverdue',
          data.myInProgressOverdue,
          data.allInProgressOverdue,
          { icon: 'error_outline', iconColor: 'red' }
        ),
      },
      {
        key: 'DueSoon',
        blocks: this.pair(
          'InProgressDueSoon',
          data.myInProgressDueSoon,
          data.allInProgressDueSoon,
          { icon: 'schedule', iconColor: 'yellow' }
        ),
      },
      {
        key: 'OnTime',
        blocks: this.pair('InProgressOnTime', data.myInProgressOnTime, data.allInProgressOnTime, {
          icon: 'sync',
          iconColor: 'cyan',
        }),
      },
    ];
  }

  private pair(
    name: string,
    mine: number | null,
    all: number | null,
    status: Pick<WidgetBlock, 'icon' | 'iconColor'>
  ): WidgetBlock[] {
    return [
      { name: `ptw.dashboard.my${name}`, value: mine },
      { name: `ptw.dashboard.all${name}`, value: all, ...status },
    ];
  }
}
